/**
 * G30 — AN ANONYMOUS SESSION MUST NOT REACH THE APPLICATION FLOW. THIS IS THE PROMPT, NOT A
 * REFUSAL.
 *
 * WHAT IS RULED, and it is the whole design of this panel. An anonymous reader who applies LINKS
 * their existing account: "Firebase keeps the same uid, and no new uid is ever created", so nothing
 * they have already done is orphaned
 * (docs/design/research/26-authors-identity-application-research.md). Proved against the
 * Firebase Auth emulator on 2026-08-27 — linking returned the same localId. `auth_provider` on the
 * platform-user row is "a last-seen record for analytics and is NEVER the gate"
 * (docs/design/04-schema.md).
 *
 * SO: NEVER SIGN THEM OUT AND NEVER MINT A SECOND ACCOUNT. A "please sign in properly" screen would
 * do both — a fresh sign-in produces a new uid and silently strands whatever the reader had.
 * ../session/link-anonymous.ts refuses a link that moves the uid rather than accepting it.
 *
 * WHY IT IS SHOWN AT THE FRONT AND NOT AT SUBMIT. The check is enforced at application-submit time,
 * against the token's sign-in-provider claim — but `submitApplication`'s declared refusals are 400,
 * 401, 409, 422 and 429, and NONE of them means "you are signed in anonymously". Verified against
 * the generated `ErrorCode` enum: no such code exists among its 46. So an anonymous reader can mint
 * a hand-off code, land in the cabinet, be told `canApply: true`, fill in the whole application and
 * be refused at the last step with a code that does not say why. Asking here costs one screen;
 * asking at the end costs somebody their afternoon. Reported as a finding.
 */
import { Alert, Anchor, Stack, Text } from '@mantine/core';
import type { TFunction } from 'i18next';
import { Trans, useTranslation } from '../../../i18n';
import { ProviderButtons } from './ProviderButtons';
import { WEBSITE_ORIGIN } from '../handoff/website-handoff';
import {
  PopupBlockedError,
  ProviderAlreadyLinkedError,
  ProviderUnreachableError,
} from '../firebase/auth-port';
import type { PopupProvider } from '../firebase/auth-port';
import { LinkChangedAccountError } from '../session/link-anonymous';
import type { LinkFailure } from '../session/AuthSessionProvider';

export interface LinkAccountPanelProps {
  readonly onLink: (provider: PopupProvider) => void;
  readonly busyProvider: PopupProvider | null;
  /** Rendered when a link attempt failed — including the uid-moved refusal, which is severe. */
  readonly error: LinkFailure | null;
}

export function LinkAccountPanel({ onLink, busyProvider, error }: LinkAccountPanelProps) {
  const { t } = useTranslation('auth');
  return (
    <Stack gap="md">
      <Stack gap="xs">
        <Text size="sm">{t('link.intro')}</Text>
        <Text size="sm" c="dimmed">
          {t('link.notASecondAccount')}
        </Text>
      </Stack>

      <ProviderButtons
        onChoose={onLink}
        busyProvider={busyProvider}
        appleLabel={t('link.addApple')}
        googleLabel={t('link.addGoogle')}
        footnote={t('link.kept')}
      />

      {error === null ? null : (
        <Alert role="alert" variant="light" color="red" title={t('link.failed.title')}>
          <Stack gap="xs">
            <Text size="sm">{linkFailureText(error.cause, t)}</Text>
            <Text size="sm" c="dimmed">
              <Trans
                t={t}
                i18nKey="link.failed.nothingChanged"
                components={{ anchor: <Anchor href={WEBSITE_ORIGIN} size="sm" rel="noopener" /> }}
              />
            </Text>
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}

/**
 * The sentence for what a link attempt threw. The port's own failures have catalogue texts; any
 * other error still shows its own message, as it always did, and a thrown non-error the generic one.
 */
function linkFailureText(cause: unknown, t: TFunction<'auth'>): string {
  if (cause instanceof LinkChangedAccountError) return t('link.failed.changedAccount');
  if (cause instanceof PopupBlockedError) return t('link.failed.popupBlocked');
  if (cause instanceof ProviderUnreachableError) return t('link.failed.providerUnreachable');
  if (cause instanceof ProviderAlreadyLinkedError) return t('link.failed.alreadyLinked');
  if (cause instanceof Error) return cause.message;
  return t('link.failed.generic');
}
